import React from 'react';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import Employee from '../../types/api/Employee';

interface VisaExpirationCardProps {
  employees: Employee[];
}

const VisaExpirationCard: React.FC<VisaExpirationCardProps> = ({ employees }) => {
  const theme = useTheme();

  // Filter employees with expiring visas (foreign students)
  const employeesWithExpiringVisas = employees
    .filter((employee) => employee.visaExpirationDate && employee.isForeignStudent)
    .map((employee) => {
      const daysRemaining = dayjs(employee.visaExpirationDate).diff(dayjs(), 'day');
      
      return {
        id: employee.id,
        name: `${employee.lastName.toUpperCase()} ${employee.firstName}`,
        daysRemaining: daysRemaining,
        visaExpirationDate: employee.visaExpirationDate!,
        quota: employee.foreignYearlyQuota,
      };
    })
    .filter((emp) => emp.daysRemaining > -1) // Only show visas not yet expired
    .sort((a, b) => a.daysRemaining - b.daysRemaining);

  const getStatusColor = (daysRemaining: number) => {
    if (daysRemaining <= 30) return '#ef4444'; // red
    if (daysRemaining <= 90) return '#f97316'; // orange
    return '#10b981'; // green
  };

  return (
    <Paper className="dp-card p-4" elevation={2}>
      <style>{`
        .scrollbar-thin::-webkit-scrollbar {
          height: 6px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: ${theme.palette.mode === 'dark' ? '#2a2a2a' : '#e5e5e5'};
          border-radius: 3px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: ${theme.palette.mode === 'dark' ? '#555' : '#999'};
          border-radius: 3px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: ${theme.palette.mode === 'dark' ? '#777' : '#666'};
        }
      `}</style>
      <h3 className="text-lg font-semibold mb-4">Expiration des visas</h3>
      {employeesWithExpiringVisas.length === 0 ? (
        <div className="text-sm text-gray-400 py-4">Aucun visa en cours</div>
      ) : (
        <div className="space-y-3">
          <div
            style={{
              display: 'flex',
              overflowX: 'auto',
              overflowY: 'hidden',
              gap: '12px',
              paddingBottom: '8px',
              scrollBehavior: 'smooth',
            }}
            className="scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200"
          >
            {employeesWithExpiringVisas.map((emp) => (
              <div
                key={emp.id}
                className="flex-shrink-0 p-3 rounded-lg"
                style={{
                  minWidth: '280px',
                  backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f5f5f5',
                }}
              >
                <div className="text-sm font-medium">{emp.name}</div>
                <div className="text-xs text-gray-500 mt-1">
                  Expiration: {new Date(emp.visaExpirationDate).toLocaleDateString('fr-FR')}
                </div>
                <div className="text-xs text-gray-500">
                  Quota: {emp.quota}h
                </div>
                <div
                  className="mt-2 text-right px-3 py-1 rounded-full font-semibold inline-block"
                  style={{
                    backgroundColor: getStatusColor(emp.daysRemaining),
                    color: 'white',
                    fontSize: '0.875rem',
                  }}
                >
                  {emp.daysRemaining} j restants
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Paper>
  );
};

export default VisaExpirationCard;
